import type { QueryClient } from '@tanstack/react-query'
import { isDocumentTranslated, MIN_WITNESS_LENGTH, wasOurTextReplaced } from './hydration-mismatch'

// A log, not a look at the DOM: before hydrateRoot is too early (hydration is concurrent, so a rewrite lands
// mid-flight) and report time is too late (React's recovery restored the text you would compare against).
// Armed by a classic inline script, because the module entry runs after the rewriter on the slow devices this
// happens on — and its text must be a CONSTANT, or TanStack's script dedup re-runs the whole bootstrap.
// childList is deliberately off: the measured wave rewrites in place, so it would be all cost and no signal.

/** Where the inline recorder parks its findings. Read only through {@link recordedTextReplacements}. */
const REWRITE_LOG_KEY = '__dcTextRewrites'

// Without it a capped log and a page that stopped mutating are the same silence, so `replacements=` would read as a total.
const REWRITE_LOG_CAPPED_KEY = '__dcTextRewritesCapped'

// A replacement logged while this was down provably predates any React DOM write; counted only, not yet acted on.
const HYDRATION_STARTED_KEY = '__dcHydrating'

// Must never be reached before a rewrite is seen; on reach the recorder raises REWRITE_LOG_CAPPED_KEY on its way out.
const MAX_RECORDS = 300

/** Enough for a witness to still match on its prefix; short enough that a page-sized node cannot bloat the log. */
const MAX_VALUE_LENGTH = 512

/** Hydration is over long before this; afterwards ordinary DOM churn must not be able to silence a report. */
const WATCH_WINDOW_MS = 10_000

// Deliberately dumb: the rules live in isForeignTextReplacement, tested, rather than stranded inside a string.
// The per-node id is what lets recordedTextReplacements rebuild each record's true `after` — nodeValue is
// sampled at flush, so every record in a batch otherwise carries the batch's final value.
export const HYDRATION_REWRITE_MONITOR_SCRIPT =
  `(function(){try{` +
  `var K='${REWRITE_LOG_KEY}',L=window[K]=window[K]||[];` +
  `if(typeof MutationObserver==='undefined')return;` +
  `var M=new WeakMap(),n=0;` +
  `var o=new MutationObserver(function(rs){for(var i=0;i<rs.length;i++){` +
  `if(L.length>=${MAX_RECORDS}){window['${REWRITE_LOG_CAPPED_KEY}']=1;o.disconnect();return}` +
  `var r=rs[i];if(r.type!=='characterData')continue;var t=r.target;` +
  `var d=M.get(t);if(d==null)M.set(t,d=n++);` +
  `L.push([t.nodeType,(r.oldValue==null?'':r.oldValue).slice(0,${MAX_VALUE_LENGTH}),(t.nodeValue==null?'':t.nodeValue).slice(0,${MAX_VALUE_LENGTH}),window['${HYDRATION_STARTED_KEY}']?0:1,d])` +
  `}});` +
  `o.observe(document,{characterData:true,characterDataOldValue:true,subtree:true});` +
  `setTimeout(function(){try{o.disconnect()}catch(e){}},${WATCH_WINDOW_MS})` +
  `}catch(e){}})();`

/** React's Suspense streaming placeholders are COMMENT nodes; only real text can be a rewrite. */
const TEXT_NODE = 3

// Pure and exported so the two exclusions below are covered by tests rather than by a DOM harness.
export function isForeignTextReplacement(nodeType: number, before: string, after: string): boolean {
  // React rewrites its Suspense placeholder comments in place, and on a streamed route those outnumber real rewrites.
  if (nodeType !== TEXT_NODE) return false
  if (before === after) return false
  // The parser appends to a streamed text node, so oldValue is a prefix: the document arriving, not being rewritten.
  if (after.startsWith(before)) return false
  return true
}

/** A replacement as it comes out of the log, carrying when it happened as well as what it did. */
export interface RecordedTextReplacement {
  readonly before: string
  readonly after: string
  /** Whether it was logged before the app called `hydrateRoot`. See {@link HYDRATION_STARTED_KEY}. */
  readonly preHydration: boolean
}

// Guarded, because failing to mark must never cost the boot.
export function markHydrationStarted(): void {
  try {
    if (typeof window === 'undefined') return
    ;(window as unknown as Record<string, unknown>)[HYDRATION_STARTED_KEY] = true
  } catch {
    // A frozen `window` costs the phase breakdown in the report, nothing else.
  }
}

/** The foreign text replacements recorded during hydration, for `wasOurTextReplaced`. */
export function recordedTextReplacements(): readonly RecordedTextReplacement[] {
  try {
    if (typeof window === 'undefined') return []
    const raw = (window as unknown as Record<string, unknown>)[REWRITE_LOG_KEY]
    if (!Array.isArray(raw)) return []
    const parsed: {
      nodeType: number
      before: string
      after: string
      preHydration: boolean
      nodeId: number | undefined
    }[] = []
    for (const entry of raw) {
      if (!Array.isArray(entry) || entry.length < 3) continue
      const [nodeType, before, after, preHydration, nodeId] = entry as [unknown, unknown, unknown, unknown, unknown]
      if (typeof nodeType !== 'number' || typeof before !== 'string' || typeof after !== 'string') continue
      parsed.push({
        nodeType,
        before,
        after,
        preHydration: preHydration === 1,
        // Missing on tuples from an older recorder: the record survives, only un-chained below.
        nodeId: typeof nodeId === 'number' ? nodeId : undefined,
      })
    }
    // The true `after` of a record is the NEXT same-node record's `before`; without this a translator rewrite that
    // React repairs in the SAME batch flattens to ours→ours and disappears from the log.
    const nextBeforeByNode = new Map<number, string>()
    for (let i = parsed.length - 1; i >= 0; i--) {
      const record = parsed[i]
      if (!record || record.nodeId === undefined) continue
      const nextBefore = nextBeforeByNode.get(record.nodeId)
      if (nextBefore !== undefined) record.after = nextBefore
      nextBeforeByNode.set(record.nodeId, record.before)
    }
    const replacements: RecordedTextReplacement[] = []
    for (const { nodeType, before, after, preHydration } of parsed) {
      if (isForeignTextReplacement(nodeType, before, after)) {
        replacements.push({ before, after, preHydration })
      }
    }
    return replacements
  } catch {
    return []
  }
}

/** Whether the recorder hit {@link MAX_RECORDS} and disconnected: the log is a floor, not a total. */
function wasRewriteLogCapped(): boolean {
  try {
    if (typeof window === 'undefined') return false
    return (window as unknown as Record<string, unknown>)[REWRITE_LOG_CAPPED_KEY] === 1
  } catch {
    return false
  }
}

/** Depth of the walk below. Query payloads here nest as `{ items: [{ title, description }] }`; three covers it. */
const MAX_WITNESS_DEPTH = 3

/** Bounded so harvesting witnesses off a large list response can never become the expensive part of a report. */
const MAX_WITNESSES = 60

// Generic rather than per-query: rebuilding specific keys would couple this to each input shape, where drift
// silently returns nothing and disables the attribution without failing anywhere.
// Markup is stripped because some payload text renders as HTML while the recorded DOM node holds the text.
function renderedTextWitnesses(queryClient: QueryClient | undefined): string[] {
  const witnesses: string[] = []
  try {
    const cache = queryClient?.getQueryCache()
    if (!cache) return []
    const visit = (value: unknown, depth: number): void => {
      if (witnesses.length >= MAX_WITNESSES) return
      if (typeof value === 'string') {
        const text = value.replace(/<[^>]*>/g, ' ').trim()
        if (text.length >= MIN_WITNESS_LENGTH) witnesses.push(text)
        return
      }
      if (depth >= MAX_WITNESS_DEPTH || typeof value !== 'object' || value === null) return
      for (const child of Array.isArray(value) ? value : Object.values(value)) {
        visit(child, depth + 1)
        if (witnesses.length >= MAX_WITNESSES) return
      }
    }
    for (const query of cache.getAll()) {
      visit(query.state.data, 0)
      if (witnesses.length >= MAX_WITNESSES) break
    }
  } catch {
    return witnesses
  }
  return witnesses
}

/**
 * How many recorded pairs {@link describeTextMismatch} names, and how long each half may be.
 *
 * Two is enough to see a pattern and short enough to sit beside the fixed-width attribution inside the
 * report's 600-char `counters` bound. 80 characters carries a heading or a label; a longer value is a
 * body of copy, where the interesting part is at the front anyway.
 */
const MAX_NAMED_REPLACEMENTS = 2
const MAX_NAMED_LENGTH = 80

/**
 * The recorded `before -> after` pairs, rendered for a report — what React's production #418 refuses to
 * say, since it builds the error with a hard-coded empty diff. See ./frontend-error-detection.md
 *
 * Truncation is marked with `…` rather than left silent, or a diff gets blamed on the wrong end.
 */
export function describeTextMismatch(replacements: readonly RecordedTextReplacement[]): string | undefined {
  if (replacements.length === 0) return undefined
  const clip = (value: string): string =>
    value.length > MAX_NAMED_LENGTH ? `${value.slice(0, MAX_NAMED_LENGTH)}…` : value
  const named = replacements
    .slice(0, MAX_NAMED_REPLACEMENTS)
    .map(({ before, after }) => `${JSON.stringify(clip(before))} -> ${JSON.stringify(clip(after))}`)
  // Say so when there were more, so two pairs are never mistaken for the whole story.
  const rest = replacements.length - named.length
  return rest > 0 ? `${named.join(' ; ')} (+${rest} more)` : named.join(' ; ')
}

/** Whether a hydration-class error belongs to whatever rewrote this document, and why we think so. */
export interface HydrationRewriteVerdict {
  /** True when the mismatch is the rewriter's, not ours: skip the report. */
  readonly foreign: boolean
  /** The reasoning, for the report's `counters` field. Always present, even when nothing was found. */
  readonly attribution: string
  /**
   * The text that actually changed, when anything was recorded. Explains an `args[]=text` mismatch only,
   * and ABSENCE IS NOT EVIDENCE OF A PRISTINE PAGE — an `args[]=HTML` one leaves no record here by
   * construction; localise those from the component stack. See ./frontend-error-detection.md
   */
  readonly mismatch: string | undefined
}

// THE gate, asked once by logFrontendError, so the boundary, the global handlers and client.tsx cannot drift onto
// different ones. Both signals fail open, so an unrecognised rewrite still reports.
// The counters ride in the report's free-form `counters` field, outside the fingerprint, so "the page was
// pristine" and "the check never ran" stop being the same silence.
export function describeHydrationRewrite(queryClient: QueryClient | undefined): HydrationRewriteVerdict {
  const witnesses = renderedTextWitnesses(queryClient)
  const replacements = recordedTextReplacements()
  const preHydration = replacements.filter((replacement) => replacement.preHydration).length
  return {
    foreign: isDocumentTranslated() || wasOurTextReplaced(replacements, witnesses),
    attribution: `rewriteCheck: witnesses=${witnesses.length} replacements=${replacements.length} pre=${preHydration} capped=${wasRewriteLogCapped() ? 1 : 0}`,
    mismatch: describeTextMismatch(replacements),
  }
}
